import { useCallback, useMemo, useState } from 'react';
import { PageBody, PageHeader } from '@/components/layout/page-header';
import { DataTable, Paginacao, TabelaPainel, type Coluna } from '@/components/patterns/data-table';
import { FilterBar, FilterBusca, FilterFlagsInstalacao, FilterLimpar, FilterSelect } from '@/components/patterns/filter-bar';
import { DetalheItem, DetalheLista } from '@/components/patterns/metric';
import { EstadoVazio } from '@/components/patterns/states';
import { InstalacaoFlags, PapelBadge, StatusContratoBadge } from '@/components/patterns/status-badge';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardEyebrow, CardHeader, CardTitle } from '@/components/ui/card';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { dataSource } from '@/data';
import type { Instalacao, ModuloContratado } from '@/data/types';
import { BREAKPOINT_TABLET, useMediaQuery } from '@/hooks/use-media-query';
import { usePaginacao } from '@/hooks/use-paginacao';
import { useResource } from '@/hooks/use-resource';
import { formatarData, plural, texto } from '@/lib/format';
import { flagDoFiltro, ordenarPorFamilia } from '@/lib/rules';
import { cn } from '@/lib/utils';

/**
 * Contratos — master-detail por instalação.
 *
 * Mesma informação da tela original (cliente proprietário, status do contrato,
 * data de contratação e módulos com data de início), com duas diferenças: a
 * saúde do módulo deixa de ser uma `<span class="chip chip-${cellvalue}">`
 * interpolada em runtime — vira badge tipado — e a lista de módulos ganha
 * cabeçalho próprio em vez de um segundo grid encaixado dentro do painel.
 */
export function ContratosPage() {
  const compacto = useMediaQuery(BREAKPOINT_TABLET);
  const carregar = useCallback((signal: AbortSignal) => dataSource.listInstalacoes(signal), []);
  const recurso = useResource<Instalacao[]>(carregar);

  const [selecionadaId, setSelecionadaId] = useState<string | null>(null);
  const [busca, setBusca] = useState('');
  const [status, setStatus] = useState<string | null>(null);
  const [ana, setAna] = useState<string | null>(null);
  const [interno, setInterno] = useState<string | null>(null);

  const instalacoes = useMemo(() => ordenarPorFamilia(recurso.dados ?? []), [recurso.dados]);

  const filtradas = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    return instalacoes.filter((item) => {
      if (status && item.cliente?.contrato?.status !== status) return false;
      if (flagDoFiltro(ana) !== undefined && item.ana !== flagDoFiltro(ana)) return false;
      if (flagDoFiltro(interno) !== undefined && item.interno !== flagDoFiltro(interno)) return false;
      if (!termo) return true;
      return `${item.nome} ${item.codigo ?? ''} ${item.cliente?.nome ?? ''}`.toLowerCase().includes(termo);
    });
  }, [instalacoes, busca, status, ana, interno]);

  const selecionada = instalacoes.find((item) => item.id === selecionadaId) ?? null;
  const filtrosAtivos = [busca.trim() !== '', status !== null, ana !== null, interno !== null].filter(Boolean).length;

  const paginacao = usePaginacao(filtradas, `${busca}|${status}|${ana}|${interno}`);

  const colunas: Array<Coluna<Instalacao>> = [
    {
      id: 'cliente',
      cabecalho: 'Cliente',
      ordenarPor: (item) => item.cliente?.nome ?? '',
      celula: (item) => (
        <div className={cn('flex flex-col gap-0.5', item.controladoraId != null && 'pl-4')}>
          <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="font-bold">{texto(item.cliente?.nome, 'Cliente sem dado')}</span>
            <InstalacaoFlags ana={item.ana} interno={item.interno} />
          </span>
          <span className="text-micro font-normal uppercase tracking-[0.04em] text-on-surface-muted">{item.nome}</span>
        </div>
      ),
    },
    { id: 'papel', cabecalho: 'Papel', largura: '170px', celula: (item) => <PapelBadge valor={item.papel} /> },
    {
      id: 'status',
      cabecalho: 'Contrato',
      largura: '190px',
      celula: (item) => <StatusContratoBadge valor={item.cliente?.contrato?.status ?? null} />,
    },
    {
      id: 'contratacao',
      cabecalho: 'Contratado em',
      largura: '160px',
      secundaria: true,
      ordenarPor: (item) => item.cliente?.contrato?.dataContratacao ?? '',
      celula: (item) => formatarData(item.cliente?.contrato?.dataContratacao),
    },
    {
      id: 'modulos',
      cabecalho: 'Módulos',
      largura: '130px',
      alinhamento: 'right',
      ordenarPor: (item) => item.cliente?.contrato?.modulos.length ?? 0,
      celula: (item) => {
        const total = item.cliente?.contrato?.modulos.length ?? 0;
        // Contrato sem módulos carregados não é "0 módulos": é dado ausente.
        if (!item.cliente?.contrato) return <Badge tone="pending">Sem dado</Badge>;
        return <span className="font-narrow text-[15px] font-bold tabular-nums">{total}</span>;
      },
    },
  ];

  const detalhe = selecionada ? <DetalheContrato instalacao={selecionada} /> : null;

  return (
    <PageBody>
      <PageHeader
        eyebrow="Parque instalado"
        titulo="Contratos"
        descricao="Cliente proprietário, situação do contrato e módulos contratados de cada instalação do escopo."
      />

      <div className={selecionada && !compacto ? 'grid items-start gap-6 xl:grid-cols-[minmax(0,1fr)_360px]' : ''}>
        <TabelaPainel
          titulo="Contratos"
          contagem={recurso.estado === 'pronto' ? plural(filtradas.length, 'resultado', 'resultados') : undefined}
          filtros={
            <FilterBar>
        <FilterBusca
          id="filtro-busca-contrato"
          label="Buscar cliente ou instalação"
          valor={busca}
          aoMudar={setBusca}
          placeholder="Nome ou código da instalação, ou nome do cliente"
        />
        <FilterSelect
          id="filtro-status-contrato"
          label="Situação do contrato"
          valor={status}
          aoMudar={setStatus}
          rotuloTodos="Qualquer situação"
          opcoes={[
            { valor: 'Ativo', rotulo: 'Ativo' },
            { valor: 'Suspenso', rotulo: 'Suspenso' },
            { valor: 'Inativo', rotulo: 'Inativo' },
          ]}
        />
        <FilterFlagsInstalacao
          idBase="filtro-contrato"
          ana={ana}
          aoMudarAna={setAna}
          interno={interno}
          aoMudarInterno={setInterno}
          mostrarAna={instalacoes.some((item) => item.ana)}
          mostrarInterno={instalacoes.some((item) => item.interno)}
        />
        <FilterLimpar
          ativo={filtrosAtivos > 0}
          quantidade={filtrosAtivos}
          aoLimpar={() => {
            setBusca('');
            setStatus(null);
            setAna(null);
            setInterno(null);
          }}
        />
            </FilterBar>
          }
          paginacao={
            recurso.estado === 'pronto' ? (
              <Paginacao
                pagina={paginacao.pagina}
                totalPaginas={paginacao.totalPaginas}
                aoMudar={paginacao.setPagina}
                tamanhoPagina={paginacao.tamanhoPagina}
                aoMudarTamanho={paginacao.setTamanhoPagina}
              />
            ) : undefined
          }
        >
            <DataTable
              rotulo="Contratos por instalação"
              itens={paginacao.visiveis}
              colunas={colunas}
              chave={(item) => item.id}
              estado={recurso.estado}
              erro={recurso.erro}
              aoTentarDeNovo={recurso.recarregar}
              selecionado={selecionadaId}
              aoSelecionar={(item) => setSelecionadaId(item.id === selecionadaId && !compacto ? null : item.id)}
              vazio={
                filtrosAtivos > 0
                  ? {
                      titulo: 'Nenhum contrato com esses filtros',
                      descricao: 'Ajuste a busca ou a situação do contrato para ver outras instalações.',
                    }
                  : {
                      titulo: 'Nenhum contrato no escopo',
                      descricao: 'Não há instalações com contrato vinculado para este usuário.',
                    }
              }
            />
        </TabelaPainel>

        {selecionada && !compacto ? (
          <Card as="aside" className="h-fit xl:sticky xl:top-6">
            <CardHeader className="flex-col items-start gap-0.5">
              <CardEyebrow>Detalhe do contrato</CardEyebrow>
              <CardTitle>{texto(selecionada.cliente?.nome, selecionada.nome)}</CardTitle>
            </CardHeader>
            <CardContent>{detalhe}</CardContent>
          </Card>
        ) : null}
      </div>

      {compacto ? (
        <Sheet open={Boolean(selecionada)} onOpenChange={(aberto) => !aberto && setSelecionadaId(null)}>
          <SheetContent>
            <SheetTitle>{texto(selecionada?.cliente?.nome, selecionada?.nome ?? 'Detalhes')}</SheetTitle>
            {detalhe}
          </SheetContent>
        </Sheet>
      ) : null}
    </PageBody>
  );
}

function DetalheContrato({ instalacao }: { instalacao: Instalacao }) {
  const contrato = instalacao.cliente?.contrato ?? null;
  const modulos = contrato?.modulos ?? [];

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap gap-2">
        <StatusContratoBadge valor={contrato?.status ?? null} />
        <PapelBadge valor={instalacao.papel} />
      </div>

      <DetalheLista>
        <DetalheItem rotulo="Instalação">{instalacao.nome}</DetalheItem>
        <DetalheItem rotulo="Contratado em">{formatarData(contrato?.dataContratacao)}</DetalheItem>
        <DetalheItem rotulo="Identificador do contrato">{texto(contrato?.id)}</DetalheItem>
        <DetalheItem rotulo="Módulos">{plural(modulos.length, 'módulo', 'módulos')}</DetalheItem>
      </DetalheLista>

      <section className="flex flex-col gap-2">
        <h3 className="pan-eyebrow">Módulos contratados</h3>
        {modulos.length === 0 ? (
          <EstadoVazio
            className="rounded-input border border-dashed border-border-strong py-8"
            titulo="Sem módulos carregados"
            descricao="O contrato desta instalação não trouxe a lista de módulos."
          />
        ) : (
          <ul className="flex flex-col divide-y divide-border-subtle overflow-hidden rounded-input border border-border">
            {modulos.map((modulo) => (
              <li key={modulo.id} className="flex items-center justify-between gap-3 px-3 py-2.5">
                <div className="flex min-w-0 flex-col gap-0.5">
                  <span className="truncate text-body">{texto(modulo.nome)}</span>
                  <span className="text-micro font-normal uppercase tracking-[0.04em] text-on-surface-muted">
                    início em {formatarData(modulo.inicio)}
                  </span>
                </div>
                <SaudeModuloBadge modulo={modulo} />
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function SaudeModuloBadge({ modulo }: { modulo: ModuloContratado }) {
  if (!modulo.saude) return <Badge tone="pending">Sem dado</Badge>;
  if (modulo.saude === 'Alinhado') return <Badge tone="success">Alinhado</Badge>;
  return <Badge tone="info">{modulo.saude}</Badge>;
}
